// Canonical site origin — single source of truth used across the OG
// library and any code that needs an absolute URL.
//
// Resolution:
//   - `NEXT_PUBLIC_SITE_URL` is REQUIRED (validated at app startup in
//     `src/lib/config/env.ts`). Build fails if unset.
//   - On the client we prefer `window.location.origin` so a custom-
//     domain visit produces same-origin URLs (matters for CSP `img-src
//     'self'` on OG previews).
//
// No code path is allowed to fall back to a hardcoded `morfi.markets`
// value. The env var IS the value.

import { env } from '@/lib/config/env';
import { addDemoModePath, stripDemoModePath } from '@/lib/network';

export function resolveCanonicalOrigin(): string {
  if (typeof window !== 'undefined') return window.location.origin;
  return env.NEXT_PUBLIC_SITE_URL;
}

/** Build a canonical absolute URL from a path. The path may start with
 *  or without a leading slash; locale prefixes are NEVER added (the
 *  site uses `localePrefix: 'never'` — locale-prefixed requests 307 to
 *  the bare URL via next-intl middleware before they ever render).
 *
 *  Always strips the `/demo-mode/` path prefix from the resulting URL so
 *  `<link rel="canonical">` points at the clean mainnet equivalent —
 *  never the devnet test surface. Callers don't have to remember. */
export function buildCanonicalUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const bare = stripDemoModePath(normalizedPath);
  const url = new URL(bare, resolveCanonicalOrigin());
  return url.toString();
}

/** Build the absolute share URL for `og:url` / `twitter:url`. Differs
 *  from `buildCanonicalUrl` by ADDING the `/demo-mode/` prefix when the
 *  surface is in demo mode — so when a recipient clicks an unfurled
 *  devnet share, they land on the demo-mode URL form (devnet content,
 *  noindex). Canonical still strips the prefix so search engines never
 *  index the demo URL. */
export function buildShareUrl(path: string, demoMode: boolean): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  // First normalise to bare so we don't double-prefix if the caller
  // already passed `/demo-mode/...`.
  const bare = stripDemoModePath(normalizedPath);
  const finalPath = demoMode ? addDemoModePath(bare) : bare;
  const url = new URL(finalPath, resolveCanonicalOrigin());
  return url.toString();
}
